import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterNotifications } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { resolveNotificationPath } from 'core/routing/deepLinks';
import { userApi } from 'features/home/home.api';
import { Button } from 'shared/components/Button';
import { Card, EmptyState, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { formatDateTime } from 'shared/utilities/format';

export function NotificationsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const query = useQuery({
    queryKey: queryKeys.notifications(page),
    queryFn: () => userApi.notifications({ page, per_page: 20 }),
    refetchOnWindowFocus: true,
  });

  async function markOne(item) {
    setError('');
    const previousLists = queryClient.getQueriesData({ queryKey: ['notifications'] });
    const previousUnread = queryClient.getQueryData(queryKeys.unreadCount);
    if (!item.is_read) {
      queryClient.setQueriesData({ queryKey: ['notifications'] }, (data) => {
        if (!data?.items) {
          return data;
        }
        return {
          ...data,
          items: data.items.map((entry) => (entry.id === item.id ? { ...entry, is_read: true } : entry)),
        };
      });
    }
    try {
      if (!item.is_read) {
        await userApi.markNotificationRead(item.id);
      }
      await invalidateAfterNotifications(queryClient, item);
      const path = resolveNotificationPath(item);
      if (path) {
        navigate(path);
      }
    } catch (err) {
      previousLists.forEach(([key, data]) => queryClient.setQueryData(key, data));
      queryClient.setQueryData(queryKeys.unreadCount, previousUnread);
      setError(humanizeApiError(err, t));
    }
  }

  async function markAll() {
    if (markingAll) {
      return;
    }
    setMarkingAll(true);
    setError('');
    const previousLists = queryClient.getQueriesData({ queryKey: ['notifications'] });
    const previousUnread = queryClient.getQueryData(queryKeys.unreadCount);
    queryClient.setQueriesData({ queryKey: ['notifications'] }, (data) => {
      if (!data?.items) {
        return data;
      }
      return { ...data, items: data.items.map((entry) => ({ ...entry, is_read: true })) };
    });
    queryClient.setQueryData(queryKeys.unreadCount, { unread_count: 0 });
    try {
      await userApi.markAllNotificationsRead();
      await invalidateAfterNotifications(queryClient);
    } catch (err) {
      previousLists.forEach(([key, data]) => queryClient.setQueryData(key, data));
      queryClient.setQueryData(queryKeys.unreadCount, previousUnread);
      setError(humanizeApiError(err, t));
    } finally {
      setMarkingAll(false);
    }
  }

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = query.data?.items || [];
  return (
    <div className="list-page">
      <PageMeta titleKey="meta.notifications" />
      <header className="page-header">
        <div>
          <h1>{t('notifications.title')}</h1>
          <p className="muted">
            <span className="nav-badge" aria-label={t('home.unread', { count: items.filter((item) => !item.is_read).length })}>
              {items.filter((item) => !item.is_read).length}
            </span>
          </p>
        </div>
        <Button tone="ghost" onClick={markAll} loading={markingAll} disabled={markingAll}>
          {t('actions.markAllRead')}
        </Button>
      </header>
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      {items.length === 0 ? <EmptyState title={t('notifications.empty')} /> : (
        <div className="card-list notifications-list">
          {items.map((item) => (
            <Card key={item.id} className={item.is_read ? 'is-read' : 'is-unread'}>
              <button type="button" className="notification-button" onClick={() => markOne(item)}>
                <div className="history-card-head">
                  <strong>{item.title}</strong>
                  <StatusChip tone={item.is_read ? 'neutral' : 'accent'}>
                    {item.is_read ? t('notifications.read') : t('notifications.unread')}
                  </StatusChip>
                </div>
                <p>{item.body}</p>
                <p className="muted">{formatDateTime(item.created_at, locale)}</p>
              </button>
            </Card>
          ))}
        </div>
      )}
      <Pagination meta={query.data?.meta} page={page} onPageChange={setPage} />
    </div>
  );
}
